import type {
  PersonRef,
  ReferenceSpec,
  VisitEffect,
  VisitEvent,
  VisitLike,
  VisitTracker,
} from './types.js';

/** Visits kept per person and board (decision C1). */
export const MAX_VISITS = 10;
/** A visit counts after this long, or earlier with a first interaction. */
export const VISIT_COUNT_AFTER_MS = 30_000;
/** Interval of heartbeats while a counted visit is open. */
export const HEARTBEAT_MS = 60_000;

export function initialVisitTracker(): VisitTracker {
  return { phase: 'idle', startedAt: null, lastBeatAt: null };
}

type Step = { tracker: VisitTracker; effects: VisitEffect[] };

const NONE: VisitEffect[] = [];

function counted(t: VisitTracker, at: number): Step {
  return { tracker: { ...t, phase: 'counted', lastBeatAt: at }, effects: ['heartbeat'] };
}

/**
 * Pure visit state machine (decision C1): idle → pending (begin) → counted after 30 s or a first
 * interaction (heartbeat, then every 60 s) → ended (end; a never counted visit is discarded).
 */
export function stepVisit(t: VisitTracker, e: VisitEvent): Step {
  const same: Step = { tracker: t, effects: NONE };
  switch (t.phase) {
    case 'idle':
      return e.type === 'start'
        ? {
            tracker: { phase: 'pending', startedAt: e.at, lastBeatAt: null },
            effects: ['begin'],
          }
        : same;
    case 'pending':
      if (e.type === 'interact') return counted(t, e.at);
      if (e.type === 'tick' && e.at - t.startedAt! >= VISIT_COUNT_AFTER_MS) return counted(t, e.at);
      if (e.type === 'end') return { tracker: { ...t, phase: 'ended' }, effects: ['discard'] };
      return same;
    case 'counted':
      if (e.type === 'end') return { tracker: { ...t, phase: 'ended' }, effects: ['end'] };
      if (e.type !== 'start' && e.at - t.lastBeatAt! >= HEARTBEAT_MS) return counted(t, e.at);
      return same;
    default:
      return same;
  }
}

function time(s: string): number {
  return Date.parse(s);
}

/** Finished visits, deduplicated (later end wins), newest first, at most {@link MAX_VISITS}. */
export function normalizeHistory(visits: readonly VisitLike[]): VisitLike[] {
  const byId = new Map<string, VisitLike>();
  for (const v of visits) {
    if (v.endedAt === null) continue;
    const prev = byId.get(v.id);
    if (prev === undefined || time(v.endedAt) > time(prev.endedAt!)) byId.set(v.id, v);
  }
  return [...byId.values()]
    .sort((a, b) => time(b.startedAt) - time(a.startedAt) || (a.id < b.id ? 1 : -1))
    .slice(0, MAX_VISITS);
}

/** The visit a reference spec points to (decision C3); null when there is none / a snapshot. */
export function referenceVisit(
  history: readonly VisitLike[],
  spec: ReferenceSpec,
  excludeId?: string,
): VisitLike | null {
  const visits = normalizeHistory(history).filter((v) => v.id !== excludeId);
  switch (spec.type) {
    case 'last':
      return visits[0] ?? null;
    case 'visit':
      return visits.find((v) => v.id === spec.id) ?? null;
    case 'date':
      return visits.find((v) => time(v.startedAt) <= time(spec.at)) ?? null;
    default:
      return null;
  }
}

/** Since when comments / open items count as new for a reference (ISO), null = no reference. */
export function referenceTime(visit: VisitLike | null, spec: ReferenceSpec): string | null {
  if (spec.type === 'snapshot') return spec.createdAt;
  if (visit !== null) return visit.startedAt;
  return spec.type === 'date' ? spec.at : null;
}

/** Key of a person within a board's visit history. */
export function identityKey(p: PersonRef): string {
  return `${p.kind}:${p.id}`;
}
